import React, { useState, useEffect } from "react";
import { Copy, X, Gift, ArrowRight, CheckCircle } from "lucide-react";

// Import the actual userStore instead of mocking it
import { useUserStore } from "store/userStore";

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ReferralModal: React.FC<ReferralModalProps> = ({ isOpen, onClose }) => {
  const { referralCode } = useUserStore();
  const baseUrl = process.env.REACT_APP_BASE_URL || "https://standbytravel.vofoxsolutions.com";
  const referralLink = referralCode ? `${baseUrl}/signup/${referralCode}` : "";

  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [isOpen]);

  if (!isOpen && !isVisible) return null;

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy text:", err);
      });
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center items-center bg-black ${isOpen ? "bg-opacity-60" : "bg-opacity-0"} transition-all duration-300 ease-in-out ${isVisible ? "opacity-100" : "opacity-0"}`}
    >
      <div
        className={`relative p-4 w-full max-w-xl transform ${isOpen ? "scale-100 translate-y-0" : "scale-95 -translate-y-4"} transition-all duration-300 ease-in-out`}
      >
        {/* Modal content */}
        <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Decorative header background */}
          <div className="absolute top-0 right-0 left-0 h-24 bg-gradient-to-r from-[#FFBC00] to-[#FFBC00] opacity-90"></div>

          {/* Modal header */}
          <div className="relative flex items-center justify-between p-5 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-md">
                <Gift className="w-6 h-6" style={{ color: "#FFBC00" }} />
              </div>
              <h3 className="text-xl font-bold text-white">Refer & Earn</h3>
            </div>
            <button type="button" className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1" onClick={onClose}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6 space-y-5">
            <div className="mt-4">
              <p className="text-gray-700 mb-1">Share with friends and both of you get rewards!</p>
              <p className="text-sm text-gray-500">When your friends sign up using your code, you'll both earn tokens.</p>
            </div>

            {/* Rewards info card */}
            <div className="bg-gradient-to-r from-[#FFF1CC] to-[#FFE7A3] rounded-xl p-4 border border-[#FFBC00]/30">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-md" style={{ backgroundColor: "#FFECB3" }}>
                  <CheckCircle className="w-4 h-4" style={{ color: "#FFBC00" }} />
                </div>
                <h4 className="font-semibold" style={{ color: "#D19A00" }}>
                  Your Rewards
                </h4>
              </div>
              <p className="ml-2 text-sm text-gray-600 pl-9">
                You get{" "}
                <span className="font-bold" style={{ color: "#D19A00" }}>
                  10 tokens
                </span>{" "}
                for each friend who joins
              </p>
            </div>

            {/* Copy Referral Code */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Your referral code</label>
              <div className="flex items-center gap-2">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    value={referralCode || ""}
                    readOnly
                    className="w-full p-3 pr-10 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-medium focus:ring-2 focus:ring-[#FFD466] focus:border-[#FFBC00] transition-all"
                  />
                </div>
                <button
                  className={`relative p-3 rounded-lg ${
                    copiedField === "code" ? "bg-green-500" : "bg-[#FFBC00]"
                  } text-white transition-colors duration-300`}
                  onClick={() => handleCopy(referralCode || "", "code")}
                >
                  {copiedField === "code" ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Copy Referral Link */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Share your referral link</label>
              <div className="flex items-center gap-2">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    value={referralLink}
                    readOnly
                    className="w-full p-3 pr-10 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 focus:ring-2 focus:ring-[#FFD466] focus:border-[#FFBC00] transition-all truncate"
                  />
                </div>
                <button
                  className={`relative p-3 rounded-lg ${
                    copiedField === "link" ? "bg-green-500" : "bg-[#FFBC00]"
                  } text-white transition-colors duration-300`}
                  onClick={() => handleCopy(referralLink, "link")}
                >
                  {copiedField === "link" ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          {/* Footer (Optional)
  <div className="p-5 pt-2">
    <button
      onClick={onClose}
      className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gradient-to-r from-[#FFBC00] to-[#D19A00] hover:from-[#E6A900] hover:to-[#C18F00] rounded-lg text-white font-medium transition-all duration-300"
    >
      Share Now <ArrowRight className="w-4 h-4" />
    </button>
  </div>
  */}
        </div>
      </div>
    </div>
  );
};

export default ReferralModal;
